import { RevealDetails, SaveRevealRequest } from "@mezo/sdk"

import { getLockData } from "#/shared/utils"

import createDappAsyncThunk from "../asyncThunk"
import { setDepositStatus } from "../slices/deposit"
import { setIsProcessingTransaction } from "../slices/wallet"

import { fetchMats } from "./mats"
import { updateBalances } from "./portal"
import { fetchAccountReferralCodeData } from "./referrals"

// Generate bridge reveal data and btc address that needs to receive BTC to be
// bridged to Ethereum. It doesn't matter who will send the BTC to the address.
export const generateBitcoinDepositDetails = createDappAsyncThunk(
  "bitcoin/generateBitcoinDepositDetails",
  async (
    {
      lockPeriod,
      bitcoinRecoveryAddress,
    }: {
      lockPeriod: number
      bitcoinRecoveryAddress: string
    },
    { extra: { sdk } },
  ) => {
    const initiatedDeposit =
      await sdk.mezo.chain?.tbtcModule.initiateBitcoinDeposit(
        lockPeriod,
        bitcoinRecoveryAddress,
      )

    return initiatedDeposit ?? null
  },
)

// Initialize BTC bridge and deposit in the Portal contract, this will save the
// deposit data in the mezo-api and mezo-api-tbtc databases.
export const createBitcoinDeposit = createDappAsyncThunk(
  "bitcoin/createBitcoinDeposit",
  async (
    {
      revealData,
      amount,
      lockPeriod,
    }: {
      revealData: SaveRevealRequest
      amount: number
      lockPeriod: number
    },
    { extra: { sdk } },
  ) => {
    const { lockStart, lockEnd } = getLockData(lockPeriod ?? 0)

    const result = await sdk.mezo.chain?.tbtcModule.createBitcoinDeposit(
      revealData,
      amount,
      { lockStart, lockEnd },
    )

    if (!result) return null

    return result.bitcoinDeposit
  },
)

// Send BTC to the deposit address generated by the bridge. This will prompt the
// wallet transaction confirmation for currently connected wallet.
// This method shouldn't be used directly! Use depositBitcoin instead or send funds
// to the deposit address manually from an external wallet.
const sendBitcoinToDepositAddress = createDappAsyncThunk(
  "bitcoin/sendBitcoinToDepositAddress",
  async (
    {
      btcDepositAddress,
      amount,
    }: { btcDepositAddress: string; amount: number },
    { extra: { sdk } },
  ) => {
    const transactionHash =
      await sdk.mezo.chain?.tbtcModule.sendBitcoinToAddress(
        btcDepositAddress,
        amount,
      )

    return transactionHash ?? null
  },
)

// Calculates amount of BTC deposited to the deposit address
export const checkBitcoinDepositedAmount = createDappAsyncThunk(
  "bitcoin/checkBitcoinDepositStatus",
  async ({ address }: { address: string }, { extra: { sdk } }) => {
    const utxos =
      await sdk.mezo.chain?.tbtcModule.findAllUnspentTransactionOutputs(address)

    if (!utxos) {
      return 0n
    }

    return utxos.reduce((acc, utxo) => acc + (utxo.value?.toBigInt() ?? 0n), 0n)
  },
)

// Bridge BTC and initialize deposit in the Portal contract in one go.
export const depositBitcoin = createDappAsyncThunk(
  "bitcoin/depositBitcoin",
  async (
    {
      amount,
      lockPeriod,
      bitcoinRecoveryAddress,
    }: {
      amount: number
      lockPeriod: number
      bitcoinRecoveryAddress: string
    },
    { dispatch },
  ) => {
    dispatch(setIsProcessingTransaction(true))
    dispatch(setDepositStatus("pending"))

    const { payload: depositDetails } = await dispatch(
      generateBitcoinDepositDetails({ lockPeriod, bitcoinRecoveryAddress }),
    )

    if (!depositDetails) {
      dispatch(setIsProcessingTransaction(false))
      dispatch(setDepositStatus("idle"))
      return false
    }

    const { btcDepositAddress, revealData } = depositDetails as RevealDetails

    const { payload: depositTransaction } = await dispatch(
      sendBitcoinToDepositAddress({ btcDepositAddress, amount }),
    )

    if (!depositTransaction) {
      dispatch(setIsProcessingTransaction(false))
      dispatch(setDepositStatus("idle"))
      return false
    }

    const { payload: depositResult } = await dispatch(
      createBitcoinDeposit({
        revealData,
        amount,
        lockPeriod,
      }),
    )

    if (!depositResult) {
      dispatch(setIsProcessingTransaction(false))
      dispatch(setDepositStatus("idle"))
      return false
    }

    await dispatch(updateBalances({ fetchOnChainData: true }))
    await dispatch(fetchAccountReferralCodeData())
    await dispatch(fetchMats())

    dispatch(setIsProcessingTransaction(false))
    dispatch(setDepositStatus("completed"))

    return true
  },
)

export const depositBitcoinFromAnotherWallet = createDappAsyncThunk(
  "bitcoin/depositBitcoinFromAnotherWallet",
  async (
    {
      amount,
      lockPeriod,
      revealData,
    }: {
      amount: number
      lockPeriod: number
      revealData: SaveRevealRequest
    },
    { dispatch },
  ) => {
    dispatch(setIsProcessingTransaction(true))

    const { payload: depositResult } = await dispatch(
      createBitcoinDeposit({
        revealData,
        amount,
        lockPeriod,
      }),
    )

    if (!depositResult) {
      dispatch(setIsProcessingTransaction(false))
      return false
    }

    await dispatch(updateBalances({ fetchOnChainData: false }))
    await dispatch(fetchAccountReferralCodeData())

    dispatch(setIsProcessingTransaction(false))

    return true
  },
)
